/**
 * FlowEditor — the structured form for one selection flow (a branch). Loads the
 * parsed spec from the backend, edits it as form sections, and saves the
 * structured spec back (the backend serializes to YAML). No YAML in the UI.
 *
 * Sections mirror the customer experience: identity → hard constraints
 * (→ chips) → scoring weights (→ bars) → must-ask questions (→ ask_user) →
 * cross-cutting questions → plot axes → defaults → disclaimers.
 */
import { useEffect, useMemo, useState } from 'preact/hooks'

import {
  createFlow,
  getFlow,
  listFlowMetrics,
  updateFlow,
} from '../../../services/flowsService.js'
import {
  DEFAULT_REPORT_SECTIONS,
  REPORT_SECTIONS,
  SYSTEM_REQUIREMENT_FIELDS,
  constraintsToRows,
  questionsToRows,
  rowsToConstraints,
  rowsToQuestions,
  rowsToWeightingQuestions,
  rowsToWeights,
  textToWeightRules,
  validateSpecClient,
  weightRulesToText,
  weightingQuestionsToRows,
  weightsToRows,
} from './flowModel.js'
import { ConstraintEditor } from './ConstraintEditor.jsx'
import { WeightsEditor } from './WeightsEditor.jsx'
import { QuestionEditor, MUST_ASK_HINT } from './QuestionEditor.jsx'
import { WeightingQuestionsEditor } from './WeightingQuestionsEditor.jsx'
import { MetricPicker } from './MetricPicker.jsx'
import { FlowKnowledgeSection } from './FlowKnowledgeSection.jsx'
import { FlowHelp } from './FlowHelp.jsx'
import { MatlabImportPanel } from './MatlabImportPanel.jsx'
import { KeyValueTable, mapToRows, rowsToMap } from './KeyValueTable.jsx'
import { FlowGuide } from './FlowGuide.jsx'

const SLUG_RE = /^[a-z][a-z0-9_]*$/

let _srSeq = 0
const nextSrId = () => `sr${++_srSeq}`

function systemRequirementsMapToRows(map) {
  return Object.entries(map || {}).map(([questionSlug, field]) => ({
    id: nextSrId(),
    questionSlug,
    field: field || '',
  }))
}

function rowsToSystemRequirementsMap(rows) {
  const out = {}
  for (const row of rows || []) {
    if (!row.questionSlug || !row.field) continue
    out[row.questionSlug.trim()] = row.field
  }
  return out
}

const REPORT_SECTION_LABELS = {
  exec_summary: 'Executive summary',
  application_profile: 'Application profile',
  ranked_shortlist: 'Ranked shortlist',
  ragone: 'Ragone plot',
  provenance: 'Provenance',
  per_cell_rationale: 'Per-cell rationale',
  pack_sizing: 'Pack sizing',
  next_steps: 'Next steps',
}

export function FlowEditor({ slug, canPublish, onClose, onSaved }) {
  const isNew = !slug

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [metrics, setMetrics] = useState([])

  const [slugInput, setSlugInput] = useState('')
  const [branchLabel, setBranchLabel] = useState('')
  const [introText, setIntroText] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [constraintRows, setConstraintRows] = useState([])
  const [weightRows, setWeightRows] = useState([])
  const [mustAskRows, setMustAskRows] = useState([])
  const [crossRows, setCrossRows] = useState([])
  const [weightingRows, setWeightingRows] = useState([])
  const [weightRulesText, setWeightRulesTextState] = useState('')
  const [weightRulesError, setWeightRulesError] = useState(null)
  const [systemRequirementsEnabled, setSystemRequirementsEnabled] = useState(false)
  const [systemRequirementsRows, setSystemRequirementsRows] = useState([])
  const [plotX, setPlotX] = useState('')
  const [plotY, setPlotY] = useState('')
  // Rows, not YAML text: these are named constants, and asking an author to be
  // a YAML parser turned a typo'd key into a flow that ranked differently with
  // no complaint. See KeyValueTable.
  const [defaultsRows, setDefaultsRows] = useState([])
  const [packAssumptionsRows, setPackAssumptionsRows] = useState([])
  const [normalization, setNormalization] = useState('zscore')
  const [disclaimersText, setDisclaimersText] = useState('')
  const [reportSections, setReportSections] = useState(DEFAULT_REPORT_SECTIONS)

  // Provenance of an imported flow (`spec.matlab_source`): while it is set, the
  // fields the import owns are read-only — the script is the source, and two
  // editable copies of the same number is the drift this feature exists to end.
  // `unlocked` is the deliberate way out, not a trap: it says out loud that the
  // next import wins.
  const [matlabSource, setMatlabSource] = useState(null)
  const [unlocked, setUnlocked] = useState(false)
  const matlabLocked = !!matlabSource && !unlocked

  /**
   * Which keys of a section the script owns, per `matlab_source.owns`.
   *
   * A flow imported BEFORE `owns` was recorded has no such list — the power-tool
   * flow on staging is one. Locking nothing there would leave the numbers freely
   * editable while the banner above them says read-only, which is the exact
   * contradiction this whole change set out to remove. So the fallback locks the
   * WHOLE section: without the list we cannot tell our keys from the script's,
   * and over-locking is visible and reversible ("Edit anyway"), while
   * under-locking is silent. One re-import replaces the guess with the truth.
   */
  const lockedKeysFor = (section, rows) => {
    if (!matlabLocked) return []
    const owns = matlabSource?.owns
    if (owns && Array.isArray(owns[section])) return owns[section]
    return rows.map((r) => r.key)
  }

  // Bumped after an import to re-read the flow. It used to be
  // `window.location.reload()`, which threw the admin out of the editor and back
  // to the AI-Management landing tab — a full page load is not a refresh of one
  // panel, and the editor is where you want to be right after importing.
  const [reloadKey, setReloadKey] = useState(0)

  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState([])
  const [warnings, setWarnings] = useState([])
  const [savedAt, setSavedAt] = useState(0)
  // A non-publisher's save is queued for approval rather than published live.
  const [queued, setQueued] = useState(false)

  const metricSet = useMemo(() => new Set(metrics.map((m) => m.column)), [metrics])

  // Load the metric catalogue + (for edit) the existing flow.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    Promise.all([listFlowMetrics(), isNew ? Promise.resolve(null) : getFlow(slug)])
      .then(([m, flow]) => {
        if (cancelled) return
        setMetrics(m.metrics || [])
        if (flow && flow.spec) {
          const s = flow.spec
          setSlugInput(flow.slug || '')
          setBranchLabel(s.branch_label || '')
          setIntroText(s.intro || '')
          setEnabled(flow.enabled !== false)
          setConstraintRows(constraintsToRows(s.hard_constraints))
          setWeightRows(weightsToRows(s.scoring_weights, s.minimize))
          setMustAskRows(questionsToRows(s.must_ask_questions))
          setCrossRows(questionsToRows(s.cross_cutting_questions))
          setWeightingRows(weightingQuestionsToRows(s.weighting_questions))
          setWeightRulesTextState(weightRulesToText(s.weight_rules))
          setWeightRulesError(null)
          setSystemRequirementsEnabled(!!s.system_requirements_enabled)
          setSystemRequirementsRows(systemRequirementsMapToRows(s.system_requirements_map))
          setPlotX(s.plot_x || '')
          setPlotY(s.plot_y || '')
          setDefaultsRows(mapToRows(s.defaults))
          setPackAssumptionsRows(mapToRows(s.pack_assumptions))
          setNormalization(s.normalization || 'zscore')
          setDisclaimersText((s.disclaimers || []).join('\n'))
          setReportSections(s.report?.default_sections || DEFAULT_REPORT_SECTIONS)
          setMatlabSource(s.matlab_source || null)
          setUnlocked(false)
        } else if (flow && !flow.parse_ok) {
          setLoadError(`This flow's YAML could not be parsed: ${flow.parse_error}`)
        }
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setLoadError(e.message)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug, isNew, reloadKey])

  // Warn on tab close while dirty.
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  // Wrap a setter so any edit marks the form dirty + clears stale messages.
  const mark = (setter) => (val) => {
    setter(val)
    setDirty(true)
    setSavedAt(0)
  }

  function assembleSpec() {
    const { scoring_weights, minimize } = rowsToWeights(weightRows)
    const spec = {
      branch_label: branchLabel.trim(),
      hard_constraints: rowsToConstraints(constraintRows),
      scoring_weights,
    }
    if (introText.trim()) spec.intro = introText.trim()
    if (minimize.length) spec.minimize = minimize
    const mustAsk = rowsToQuestions(mustAskRows)
    if (mustAsk.length) spec.must_ask_questions = mustAsk
    const cross = rowsToQuestions(crossRows)
    if (cross.length) spec.cross_cutting_questions = cross
    const weighting = rowsToWeightingQuestions(weightingRows)
    if (weighting.length) spec.weighting_questions = weighting
    const { rules: weightRules } = textToWeightRules(weightRulesText)
    if (weightRules.length) spec.weight_rules = weightRules
    if (systemRequirementsEnabled) spec.system_requirements_enabled = true
    const srMap = rowsToSystemRequirementsMap(systemRequirementsRows)
    if (Object.keys(srMap).length) spec.system_requirements_map = srMap
    const defaults = rowsToMap(defaultsRows)
    if (Object.keys(defaults).length) spec.defaults = defaults
    // buildSpec assembles the spec key by key, so a block the editor doesn't
    // know is DROPPED on save. pack_assumptions carries the pack physics —
    // losing it silently would move every ranking back to the code defaults
    // while the flow still looked correct.
    const packAssumptions = rowsToMap(packAssumptionsRows)
    if (Object.keys(packAssumptions).length) spec.pack_assumptions = packAssumptions
    // Emitted only when it diverges from the default, so a flow that never
    // touched it serialises byte-identically to before.
    if (normalization && normalization !== 'zscore') spec.normalization = normalization
    if (plotX) spec.plot_x = plotX
    if (plotY) spec.plot_y = plotY
    const disclaimers = disclaimersText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    if (disclaimers.length) spec.disclaimers = disclaimers
    if (reportSections.length) spec.report = { default_sections: reportSections }
    // Same reason as pack_assumptions above: this function rebuilds the spec key
    // by key, so anything it does not name is dropped on save. Losing the
    // provenance would leave a flow whose numbers came from a script with no
    // record of which one — and the editor would silently unlock itself.
    if (matlabSource) spec.matlab_source = matlabSource
    return spec
  }

  // The slug → requirement mapping, seen from the question rather than from its
  // own table. Storage is unchanged (`systemRequirementsRows` →
  // `system_requirements_map`); only the way in changed.
  const requirementOf = Object.fromEntries(
    systemRequirementsRows
      .filter((r) => r.questionSlug)
      .map((r) => [r.questionSlug, r.field])
  )

  function handleRequirementChange(slug, field) {
    if (!slug) return
    const rest = systemRequirementsRows.filter((r) => r.questionSlug !== slug)
    // An empty choice REMOVES the mapping rather than storing a blank field —
    // a row pointing at nothing used to survive saves and read as "mapped".
    mark(setSystemRequirementsRows)(
      field ? [...rest, { id: nextSrId(), questionSlug: slug, field }] : rest
    )
  }

  function handleWeightRulesInput(text) {
    mark(setWeightRulesTextState)(text)
    const { error } = textToWeightRules(text)
    setWeightRulesError(error)
  }

  async function handleSave() {
    const spec = assembleSpec()
    const clientErrors = []
    if (isNew && !SLUG_RE.test(slugInput)) {
      clientErrors.push('Slug must be snake_case and start with a letter (e.g. forklift).')
    }
    if (weightRulesError) {
      clientErrors.push(`Weight rules (advanced): ${weightRulesError}`)
    }
    const { errors: specErrors } = validateSpecClient(spec, metricSet)
    clientErrors.push(...specErrors)
    if (clientErrors.length) {
      setErrors(clientErrors)
      setWarnings([])
      return
    }
    setSaving(true)
    setErrors([])
    try {
      const res = isNew
        ? await createFlow({ slug: slugInput, spec })
        : await updateFlow(slug, { spec, enabled })
      setWarnings(res.warnings || [])
      setQueued(!!res.queued)
      setDirty(false)
      setSavedAt(Date.now())
      onSaved && onSaved()
    } catch (e) {
      setErrors(e.detail?.errors?.length ? e.detail.errors : [e.message])
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    if (dirty && !window.confirm('Discard unsaved changes to this flow?')) return
    onClose && onClose()
  }

  if (loading) {
    return <div className="admin-aim-subtab-body admin-flow-detail">Loading flow…</div>
  }
  if (loadError) {
    return (
      <div className="admin-aim-subtab-body admin-flow-detail">
        <div className="admin-aim-error-banner">{loadError}</div>
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={handleCancel}>
          ← Back
        </button>
      </div>
    )
  }

  return (
    <div className="admin-aim-subtab-body admin-flow-detail">
      <div className="admin-flow-detail__bar">
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={handleCancel}>
          ← Back
        </button>
        <h2 className="admin-aim-title admin-flow-detail__title">
          {isNew ? 'New flow' : branchLabel || slug}
          {dirty && <span className="admin-flow-dirty"> ● unsaved</span>}
          {!dirty && savedAt > 0 && (
            <span className="admin-flow-saved"> {queued ? '✓ submitted for review' : '✓ saved'}</span>
          )}
        </h2>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--send"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : canPublish ? 'Save' : 'Submit for review'}
        </button>
      </div>

      {!canPublish && (
        <div className="admin-aim-warning admin-flow-note">
          Your edits are submitted for review — the flow goes live once an approver
          publishes it. Track the status under <strong>My Submissions</strong>.
        </div>
      )}
      <p className="admin-flow-note admin-flow-note--muted">
        {canPublish
          ? 'Changes go live to the assistant within ~1 minute (the knowledge cache invalidates on save) — no deploy.'
          : 'Approved changes go live to the assistant within ~1 minute of publishing — no deploy.'}
      </p>

      {errors.length > 0 && (
        <div className="admin-aim-error-banner">
          <strong>Fix before saving:</strong>
          <ul>
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="admin-aim-warning">
          <strong>Saved with warnings:</strong>
          <ul>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Identity */}
      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">
          Identity <FlowHelp k="identity" />
        </h3>
        <p className="admin-flow-hint">
          The <strong>branch label</strong> is the name customers see (e.g. on the
          Cell Selection start screen). The <strong>slug</strong> is a permanent
          internal key — it can't be changed once the flow exists.
        </p>
        <div className="admin-aim-field-grid">
          <label className="admin-aim-field">
            <span className="admin-aim-label">Branch label</span>
            <input
              className="admin-aim-input"
              type="text"
              value={branchLabel}
              placeholder="e.g. Power Tool"
              onInput={(e) => mark(setBranchLabel)(e.currentTarget.value)}
            />
          </label>
          <label className="admin-aim-field">
            <span className="admin-aim-label">Slug</span>
            <input
              className="admin-aim-input"
              type="text"
              value={isNew ? slugInput : slug}
              placeholder="snake_case (e.g. forklift)"
              disabled={!isNew}
              onInput={(e) => mark(setSlugInput)(e.currentTarget.value)}
            />
          </label>
          {!isNew && (
            <label className="admin-aim-checkbox admin-flow-enabled">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => mark(setEnabled)(e.currentTarget.checked)}
              />
              <span>Enabled (visible to customers)</span>
            </label>
          )}
        </div>
        <label className="admin-aim-field admin-flow-intro">
          <span className="admin-aim-label">
            Intro (customer-visible) <FlowHelp k="intro" />
          </span>
          <textarea
            className="admin-aim-textarea"
            rows={2}
            placeholder="A short markdown intro shown to the customer at the start of this branch."
            value={introText}
            onInput={(e) => mark(setIntroText)(e.currentTarget.value)}
          />
          <span className="admin-aim-field-help">
            Markdown. Customer-visible — shown before the application profile.
          </span>
        </label>
      </section>

      <FlowGuide matlabSource={matlabSource} />

      {!isNew && (
        <MatlabImportPanel
          slug={slug}
          onImported={() => {
            setReloadKey((k) => k + 1)
            setDirty(false)
          }}
        />
      )}

      {matlabSource && (
        <p className="admin-flow-provenance" role="note">
          The filters and weights below come from{' '}
          <code>{matlabSource.file}</code>, imported {matlabSource.imported_at}
          {matlabSource.imported_by ? ` by ${matlabSource.imported_by}` : ''} —
          they are read-only, because the script is the source. Change them
          there and import again.{' '}
          <button
            type="button"
            className="admin-ai-btn admin-ai-btn--ghost"
            onClick={() => setUnlocked((v) => !v)}
          >
            {unlocked ? 'Lock again' : 'Edit anyway'}
          </button>
          {unlocked && (
            <strong>
              {' '}
              Unlocked: whatever you change here is overwritten by the next
              import.
            </strong>
          )}
        </p>
      )}

      <ConstraintEditor
        rows={constraintRows}
        metrics={metrics}
        readOnly={matlabLocked}
        onChange={mark(setConstraintRows)}
      />
      <WeightsEditor
        rows={weightRows}
        metrics={metrics}
        readOnly={matlabLocked}
        onChange={mark(setWeightRows)}
      />

      <QuestionEditor
        title="Fragen an den Kunden"
        hint={MUST_ASK_HINT}
        questions={mustAskRows}
        onChange={mark(setMustAskRows)}
        requirementFields={SYSTEM_REQUIREMENT_FIELDS}
        requirementOf={requirementOf}
        onRequirementChange={handleRequirementChange}
      />

      <WeightingQuestionsEditor
        questions={weightingRows}
        metrics={metrics}
        onChange={mark(setWeightingRows)}
      />

      {/* Everything below is the "rarely touched" zone. A native <details> so it
          collapses without state, and so a browser find-in-page still opens it.
          These are not superfluous — Ion reads all of them — they are simply not
          what anyone opens this editor to change. */}
      <details className="admin-flow-rare">
        <summary className="admin-flow-rare__summary">
          Selten gebraucht — Plot-Achsen, Hinweise, Report, Rahmenfragen,
          Gewichts-Regeln
        </summary>

      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">
          Plot axes (Ragone) <FlowHelp k="plotAxes" />
        </h3>
        <p className="admin-flow-hint">
          The two metrics Ion plots the candidate cells on for this branch. By
          convention X = power, Y = energy.
        </p>
        <div className="admin-aim-field-grid">
          <label className="admin-aim-field">
            <span className="admin-aim-label">X axis</span>
            <MetricPicker value={plotX} metrics={metrics} ariaLabel="Plot X axis" onChange={mark(setPlotX)} />
          </label>
          <label className="admin-aim-field">
            <span className="admin-aim-label">Y axis</span>
            <MetricPicker value={plotY} metrics={metrics} ariaLabel="Plot Y axis" onChange={mark(setPlotY)} />
          </label>
        </div>
      </section>

      <QuestionEditor
        title="Rahmenfragen (optional)"
        hint="Fragen, die über den ganzen Flow gelten statt einen Schritt zu sein — z. B. Beschaffungsregion oder Budget."
        questions={crossRows}
        onChange={mark(setCrossRows)}
      />

      {/* Weight rules (advanced) */}
      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">
          Weight rules (advanced) <FlowHelp k="weightRules" />
        </h3>
        <p className="admin-flow-hint">
          Declarative weight adjustments driven by a question answer — edited as
          raw JSON (array of rules). Each rule needs <code>when.question</code>,{' '}
          <code>adjust</code> (metric → delta) and a <code>reason</code>.
        </p>
        <textarea
          className="admin-aim-textarea admin-aim-monospace"
          rows={8}
          placeholder={
            '[\n  {\n    "when": { "question": "priority", "equals": "range" },\n    "adjust": { "electrical_p_peak_grav": 0.1 },\n    "reason": "Customer prioritized range."\n  }\n]'
          }
          value={weightRulesText}
          onInput={(e) => handleWeightRulesInput(e.currentTarget.value)}
        />
        {weightRulesError && <p className="admin-aim-error">{weightRulesError}</p>}
      </section>

      {/* The pack-sizing gate. The slug → field MAPPING that used to live here
          as its own table now sits on the question it belongs to ("Antwort
          setzt" in QuestionEditor): reading two places to understand one
          question is what made this editor unreadable, and a mapping kept apart
          from its question could point at a slug that no longer existed. Only
          the on/off switch is left, because it is a property of the branch
          rather than of any one question. */}
      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">
          Pack-Mathematik <FlowHelp k="systemRequirements" />
        </h3>
        <p className="admin-flow-hint">
          Wenn eingeschaltet, rechnet Ion aus den Antworten das Pack durch
          (Topologie, Gewicht, Volumen, Reichweite oder Laufzeit) und schließt
          Zellen aus, die es nicht bauen können. Welche Antwort welche Größe
          setzt, steht bei der jeweiligen Frage unter „Antwort setzt".
        </p>
        <label className="admin-aim-checkbox admin-flow-sr-enabled">
          <input
            type="checkbox"
            checked={systemRequirementsEnabled}
            onChange={(e) => mark(setSystemRequirementsEnabled)(e.currentTarget.checked)}
          />
          <span>Pack-Mathematik für diesen Flow verwenden</span>
        </label>
      </section>

      {/* Defaults + disclaimers */}
      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">
          Defaults <FlowHelp k="defaults" />
        </h3>
        <p className="admin-flow-hint">
          The values Ion proposes when the customer has not stated one. A value
          here only takes effect if a question maps to it — otherwise it is
          inert, which is why the import refuses to leave one unasked.
        </p>
        <KeyValueTable
          rows={defaultsRows}
          onChange={mark(setDefaultsRows)}
          lockedKeys={lockedKeysFor('defaults', defaultsRows)}
          addLabel="+ Add default"
          keyPlaceholder="ambient_c"
          valuePlaceholder="25"
          emptyHint="No defaults — Ion asks for everything."
        />
      </section>
      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">Pack assumptions</h3>
        <p className="admin-flow-hint">
          The named constants of the cell → pack step. Leave empty to use the
          defaults (peak/cont derating 1.2, weight efficiency 0.70 — pouch 0.50,
          volume efficiency 0.50, energy basis <code>w_c10</code>, series
          rounding <code>round</code>). These shape every pack figure and every
          weight/volume budget check — an unknown key is rejected on save rather
          than ignored.
        </p>
        <KeyValueTable
          rows={packAssumptionsRows}
          onChange={mark(setPackAssumptionsRows)}
          lockedKeys={lockedKeysFor('pack_assumptions', packAssumptionsRows)}
          addLabel="+ Add assumption"
          keyPlaceholder="cont_power_derating"
          valuePlaceholder="1.3"
          emptyHint="Empty — pack_math's defaults apply (MS's reference values)."
        />
        <label className="admin-flow-field">
          <span className="admin-flow-label">Score normalisation</span>
          <select
            className="admin-aim-select"
            value={normalization}
            disabled={matlabLocked}
            onChange={(e) => mark(setNormalization)(e.currentTarget.value)}
          >
            <option value="zscore">z-score (spread-relative, outlier-robust)</option>
            <option value="minmax">min-max 0..1 (matches the MATLAB reference)</option>
          </select>
        </label>
      </section>
      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">
          Disclaimers <FlowHelp k="disclaimers" />
        </h3>
        <p className="admin-flow-hint">
          One per line. Mandatory for branches that rank on proxy metrics or have data gaps.
        </p>
        <textarea
          className="admin-aim-textarea"
          rows={3}
          placeholder="Cycle life is not in the AI catalogue — ranking uses available metrics."
          value={disclaimersText}
          onInput={(e) => mark(setDisclaimersText)(e.currentTarget.value)}
        />
      </section>

      {/* Report defaults */}
      <section className="admin-aim-section admin-flow-section">
        <h3 className="admin-aim-section-title">
          Report defaults <FlowHelp k="reportDefaults" />
        </h3>
        <p className="admin-flow-hint">
          Sections included in the generated report for this branch by default.
        </p>
        <div className="admin-aim-checkbox-group">
          {REPORT_SECTIONS.map((id) => (
            <label key={id} className="admin-aim-checkbox">
              <input
                type="checkbox"
                checked={reportSections.includes(id)}
                onChange={(e) => {
                  const next = e.currentTarget.checked
                    ? [...reportSections, id]
                    : reportSections.filter((s) => s !== id)
                  mark(setReportSections)(next)
                }}
              />
              <span>{REPORT_SECTION_LABELS[id]}</span>
            </label>
          ))}
        </div>
      </section>
      </details>

      {/* Flow-specific knowledge — only for a saved flow (needs a slug to tag). */}
      {isNew ? (
        <section className="admin-aim-section admin-flow-section admin-flow-knowledge">
          <h3 className="admin-aim-section-title">
            Knowledge (flow-specific) <FlowHelp k="knowledge" />
          </h3>
          <p className="admin-flow-hint">
            Save the flow first — knowledge entries are tagged with its slug.
          </p>
        </section>
      ) : (
        <FlowKnowledgeSection slug={slug} canPublish={canPublish} />
      )}
    </div>
  )
}
